import { BodyBox, NotFoundPage, useEntityForm } from '@lbcamden/lib-react';
import { DateTime } from 'luxon';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { PageBreadcrumbs } from '~/client/components/shared/PageBreadcrumbs';
import {
  useCurrentPeopleList,
  useCurrentUser,
  useNoteSubtypeSelectOptions,
  useProperty,
  usePropertyDetails,
  useReferenceListDisplayValue,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { useGoBackOrNavigate } from '~/client/hooks/useGoBackOrNavigate';
import { getPropertyBreadcrumbs, toNotePage, toPropertyPage } from '~/client/utils/navigation';
import { noteCautionSubtype } from '~/common/config/notes';
import { tempNumericId } from '~/common/tinybase/utils';
import { NewNotePayload, UsedNoteType } from '~/common/types/notes';
import { getFullNoteText, getNoteSubject } from '~/common/utils/notes';

import {
  createNoteAssociationOptions,
  newNoteFormConfig,
  newNoteFormSchema,
  NewNoteFormValues,
} from './config';

const title = 'New note';

export const NoteCreatePage: FunctionComponent = () => {
  const user = useCurrentUser();

  const { propertyId: propertyIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);

  const navigate = useGoBackOrNavigate();

  const [selectedType, setSelectedType] = useState<UsedNoteType>();
  const [isCaution, setIsCaution] = useState(false);

  const userStore = useUserStore();
  const refListDisplayValue = useReferenceListDisplayValue();
  const subtypeOptions = useNoteSubtypeSelectOptions(selectedType);
  const property = useProperty(propertyId);
  const propertyDetails = usePropertyDetails(propertyId);
  const people = useCurrentPeopleList(propertyId);

  const { propertyRef = '' } = property || {};
  const { payReference = '' } = propertyDetails || {};

  const noteAssociationOptions = useMemo(
    () => createNoteAssociationOptions(propertyRef, payReference, people),
    [propertyRef, payReference, people],
  );

  const formConfig = useMemo(
    () => newNoteFormConfig(noteAssociationOptions, subtypeOptions, !!selectedType, isCaution),
    [noteAssociationOptions, subtypeOptions, selectedType, isCaution],
  );

  const goToPropertyPage = () => navigate(toPropertyPage(propertyId));

  const getPersonFullName = (personId: number) =>
    people.find((person) => person.personId === personId)?.fullName;

  const onSave = ({
    attachTo: { type, entityRef },
    subtype = '',
    ...noteValues
  }: NewNoteFormValues) => {
    const subject = getNoteSubject(
      { entityRef, type, subtype },
      refListDisplayValue,
      getPersonFullName,
    );

    const payload: NewNotePayload = {
      ...noteValues,
      propertyId,
      entityRef,
      type,
      subtype,
      subject,
      noteId: String(tempNumericId()),
      note: getFullNoteText(user!, noteValues.note),
      createdBy: 'Home Visits',
      created: DateTime.now().toISO(),
    };

    userStore?.handleAndDispatchMessage('createNote', propertyId, payload, {
      newEntityTempId: payload.noteId,
    });

    void navigate(toNotePage(propertyId, payload.noteId));
  };

  const {
    formState: {
      data: { attachTo, caution, subtype },
      updateField,
    },
    form,
  } = useEntityForm({
    title,
    schema: newNoteFormSchema,
    config: formConfig,
    initialValue: { caution: false },
    onCancel: goToPropertyPage,
    onSave,
  });

  useEffect(() => {
    if (attachTo) {
      setSelectedType(attachTo.type);
      updateField('caution', false);
      updateField('subtype', undefined);
    }
  }, [attachTo]);

  useEffect(() => {
    setIsCaution(!!caution);
    updateField('subtype', caution ? noteCautionSubtype : undefined);
  }, [caution]);

  useEffect(() => {
    if (subtype === noteCautionSubtype) {
      updateField('caution', true);
    }
  }, [subtype]);

  if (!property) {
    return <NotFoundPage />;
  }

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[...getPropertyBreadcrumbs(property), { name: title }]}
      />
      {form}
    </BodyBox>
  );
};
